import React, { useCallback, useEffect, useState } from 'react'
import ScreenHeader from '../../../components/v2/ScreenHeader'
import Title from '../../../components/v2/Title'
import ProgressBar from '../../../components/v2/ProgressBar'
import Colors from '../../../assets/styles/Colors'
import styled from 'styled-components'
import Responsive from '../../../assets/config/Responsive'
import NavigationButton from '../../../components/v2/buttons/NavigationButton'
import { useNavigate } from 'react-router-dom'
import { useAppSelector } from '../../../store'
import { SliceType, setCompanyFields } from '../../../store/businessSlice'
import Input from '../../../components/v2/Input'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useDispatch } from 'react-redux'
import * as Yup from 'yup'
import { useFormik } from 'formik'
import api from '../../../api'
import Loader from '../../../components/Loader'
import SuccessIcon from '../../../assets/icons/JSX/SuccessIcon'
import { Link } from 'react-router-dom'
import BaseModal from '../../../components/v2/modals/BaseModal'
import BackgroundIcon from '../../../components/v2/BackgroundIcon'

const CompanyContactInfo = () => {
  //
  const navigate = useNavigate()
  //
  const { t } = useTranslation()
  //
  const dispatch = useDispatch()
  //
  const [showSuccesful, setShowSuccesful] = useState<boolean>(false)
  //
  const { productType, companyInfo } = useAppSelector((data: { business: SliceType }) => data.business)
  //
  const [sendBusinessHealth, { isLoading: healthLoading }] = api.useSendBusinessHealthInfoMutation()
  const [sendBusinessCar, { isLoading: carLoading }] = api.useSendBusinessCarInfoMutation()
  const [sendBusinessProperty, { isLoading: propertyLoading }] = api.useSendBusinessPropertyInfoMutation()
  const [sendBusinessOther, { isLoading: otherLoading }] = api.useSendBusinessOtherInfoMutation()
  //

  // DATA
  const pageData = [
    { id: 1, title: `${t('companyInfo')}`, url: `/${productType}-company-info`, active: false, passed: true },
    { id: 2, title: `${t('contactPersonInfo')}`, url: '/company-contact-info', active: true, passed: false },
  ]
  const headerCount = {
    passedCount: 2,
    totalCount: 2,
  }
  // DATA

  // VALIDATION

  // regex
  const letterRegex = /^[A-Za-z]+$|^[\u10D0-\u10FF]*$/
  const letterSpaceRegex = /^[ა-ჰa-zA-Z\s]*$/
  const spaceRegex = /^\S*$/
  // regex

  const PersonInfoValidationSchema = Yup.object({
    firstName: Yup.string()
      .min(2, `${t('minNameLetterLenght')}`)
      .matches(letterRegex, `${t('enterGeoOrEngLettersOrRemoveSpace')}`)
      .required(`${t('required')}`),
    lastName: Yup.string()
      .min(2, `${t('minSurnameLetterLenght')}`)
      .matches(letterRegex, `${t('enterGeoOrEngLettersOrRemoveSpace')}`)
      .required(`${t('required')}`),
    position: Yup.string()
      .min(2, `${t('minTwoSymbolos')}`)
      .matches(letterSpaceRegex, `${t('enterGeorgianOrEnglishLetters')}`)
      .required(`${t('required')}`),
    email: Yup.string()
      .matches(spaceRegex, `${t('spacevalidation')}`)
      .email(`${t('enterValidMail')}`)
      .required(`${t('required')}`),
    mobile: Yup.string()
      .matches(spaceRegex, `${t('spacevalidation')}`)
      .min(9, `${t('enterValidPhone')}`)
      .max(9, `${t('enterValidPhone')}`)
      .matches(/^5\d{8}$/, `${t('validPhoneNumber')}`)
      .required(`${t('required')}`),
  })

  const initialValues = {
    firstName: companyInfo.firstName || '',
    lastName: companyInfo.lastName || '',
    position: companyInfo.position || '',
    email: companyInfo.email || '',
    mobile: companyInfo.mobile || '',
  }

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: PersonInfoValidationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      console.log(values)
    },
  })

  // VALIDATION

  // auto navigate bussiness category
  const backToCategories = useCallback(() => {
    if (!productType) {
      navigate('/business-category')

      toast.info('მოხდა ავტომატური გადამისამართება !')
    }
  }, [productType, navigate])
  //
  useEffect(() => {
    backToCategories()
  }, [backToCategories])
  //

  const sendHealthInsuranceInfo = async () => {
    try {
      await sendBusinessHealth({
        companyName: companyInfo.companyName,
        idNumber: companyInfo.companyID,
        activityField: companyInfo.companyActivity,
        comment: companyInfo.comment,
        firstName: companyInfo.firstName,
        lastName: companyInfo.lastName,
        position: companyInfo.position,
        email: companyInfo.email,
        mobileNumber: companyInfo.mobile,
        type: productType,
        otherInformation: {
          employeesCount: companyInfo.employeeNumber,
          insuredEmployeesCount: companyInfo.insuredEmployee,
          topManagementCount: companyInfo.managementNumber,
          averageAge: companyInfo.employeeAverageAge,
          genderMale: companyInfo.genderBalance?.genderMale,
          genderFemale: companyInfo.genderBalance?.genderFemale,
          minInsurancePackage: companyInfo.minPriceInsurance,
          insurancePayer: companyInfo.insurancePayer,
          usedInsuranceLastYear: companyInfo.insuredRecently,
          insuranceCompanyName: companyInfo.insuredCompanyName,
          contractRenewalDate: companyInfo.contractRenewal,
        },
      }).unwrap()

      setShowSuccesful(true)
    } catch (error: any) {
      if (error?.data?.errors?.constructor === Array) {
        const firstError = error.data.errors[0]
        const errorMessage = firstError.message
        toast.error(errorMessage)
      } else {
        toast.error(`${t('anErrorOccurred')}`)
      }
    }
  }

  const sendCarInsuranceInfo = async () => {
    try {
      await sendBusinessCar({
        companyName: companyInfo.companyName,
        idNumber: companyInfo.companyID,
        activityField: companyInfo.companyActivity,
        comment: companyInfo.comment,
        firstName: companyInfo.firstName,
        lastName: companyInfo.lastName,
        position: companyInfo.position,
        email: companyInfo.email,
        mobileNumber: companyInfo.mobile,
        type: productType,
        otherInformation: {
          carBrand: companyInfo.carBrand,
          carModel: companyInfo.carModel,
          manufactureYear: companyInfo.manufactoreYear,
          purpose: companyInfo.purpose,
          currency: companyInfo.currency,
          marketValue: companyInfo.marketValue,
        },
      }).unwrap()

      setShowSuccesful(true)
    } catch (error: any) {
      if (error?.data?.errors?.constructor === Array) {
        const firstError = error.data.errors[0]
        const errorMessage = firstError.message
        toast.error(errorMessage)
      } else {
        toast.error(`${t('anErrorOccurred')}`)
      }
    }
  }

  const sendPropertyInsuranceInfo = async () => {
    try {
      await sendBusinessProperty({
        companyName: companyInfo.companyName,
        idNumber: companyInfo.companyID,
        activityField: companyInfo.companyActivity,
        comment: companyInfo.comment,
        firstName: companyInfo.firstName,
        lastName: companyInfo.lastName,
        position: companyInfo.position,
        email: companyInfo.email,
        mobileNumber: companyInfo.mobile,
        type: productType,
        otherInformation: {
          cadastralCode: companyInfo.cadastralCode,
          propertyType: companyInfo.propertyType,
          insurePropertyContents: companyInfo.propertyContents,
          address: companyInfo.propertyAddress,
          purpose: companyInfo.propertyPurpose,
          currency: companyInfo.currency,
          marketValue: companyInfo.marketValue,
        },
      }).unwrap()

      setShowSuccesful(true)
    } catch (error: any) {
      if (error?.data?.errors?.constructor === Array) {
        const firstError = error.data.errors[0]
        const errorMessage = firstError.message
        toast.error(errorMessage)
      } else {
        toast.error(`${t('anErrorOccurred')}`)
      }
    }
  }

  const sendOtherInsuranceInfo = async () => {
    try {
      await sendBusinessOther({
        companyName: companyInfo.companyName,
        idNumber: companyInfo.companyID,
        activityField: companyInfo.companyActivity,
        comment: companyInfo.comment,
        firstName: companyInfo.firstName,
        lastName: companyInfo.lastName,
        position: companyInfo.position,
        email: companyInfo.email,
        mobileNumber: companyInfo.mobile,
        type: productType,
        otherInformation: {
          insuranceProduct: companyInfo.insuranceProduct,
        },
      }).unwrap()

      setShowSuccesful(true)
    } catch (error: any) {
      if (error?.data?.errors?.constructor === Array) {
        const firstError = error.data.errors[0]
        const errorMessage = firstError.message
        toast.error(errorMessage)
      } else {
        toast.error(`${t('anErrorOccurred')}`)
      }
    }
  }

  const handleSendBtn = () => {
    productType === 'health'
      ? sendHealthInsuranceInfo()
      : productType === 'car'
      ? sendCarInsuranceInfo()
      : productType === 'property'
      ? sendPropertyInsuranceInfo()
      : productType === 'other'
      ? sendOtherInsuranceInfo()
      : backToCategories()
  }

  const mainLoading = healthLoading || carLoading || propertyLoading || otherLoading

  return mainLoading ? (
    <Loader />
  ) : (
    <Wrapper>
      {showSuccesful && (
        <BaseModalStyle showCloseBtn={false} isOpen={showSuccesful} onClose={() => {}}>
          <SuccessModalWrapper>
            <SuccessIconStyle />
            <Text>ინფორმაცია წარმატებით გაიგზავნა</Text>
            <LinkStyle to="/business">დაბრუნება</LinkStyle>
          </SuccessModalWrapper>
        </BaseModalStyle>
      )}
      <ScreenHeader totalCount={headerCount.totalCount} passedCount={headerCount.passedCount} />
      <Content>
        <Title text={t('contactPersonInfo')} />
        <ProgressBar pageObj={pageData} />
        <WhiteBox>
          <Input
            id="business-person-name"
            label={t('firstName')}
            required={true}
            width={'fullWidth'}
            value={companyInfo.firstName || ''}
            onChange={(value) => [
              dispatch(
                setCompanyFields({
                  fieldName: 'firstName',
                  fieldValue: value,
                }),
              ),
              formik.setFieldValue('firstName', value),
            ]}
            error={formik.errors.firstName && companyInfo?.firstName ? true : false}
            errorText={formik.errors.firstName}
            onBlur={formik.handleBlur}
          />
          <Input
            id="business-person-surname"
            label={t('lastName')}
            required={true}
            width={'fullWidth'}
            value={companyInfo.lastName || ''}
            onChange={(value) => [
              dispatch(
                setCompanyFields({
                  fieldName: 'lastName',
                  fieldValue: value,
                }),
              ),
              formik.setFieldValue('lastName', value),
            ]}
            error={formik.errors.lastName && companyInfo?.lastName ? true : false}
            errorText={formik.errors.lastName}
            onBlur={formik.handleBlur}
          />
          <Input
            id="business-person-position"
            label={t('position')}
            required={true}
            width={'fullWidth'}
            value={companyInfo.position || ''}
            onChange={(value) => [
              dispatch(
                setCompanyFields({
                  fieldName: 'position',
                  fieldValue: value,
                }),
              ),
              formik.setFieldValue('position', value),
            ]}
            error={formik.errors.position && companyInfo?.position ? true : false}
            errorText={formik.errors.position}
            onBlur={formik.handleBlur}
          />
          <Input
            id="business-person-email"
            label={t('email')}
            required={true}
            width={'fullWidth'}
            type="email"
            value={companyInfo.email || ''}
            onChange={(value) => [
              dispatch(
                setCompanyFields({
                  fieldName: 'email',
                  fieldValue: value,
                }),
              ),
              formik.setFieldValue('email', value),
            ]}
            error={formik.errors.email && companyInfo?.email ? true : false}
            errorText={formik.errors.email}
            onBlur={formik.handleBlur}
          />
          <Input
            id="business-person-phone"
            label={t('mobile')}
            required={true}
            width={'fullWidth'}
            type="number"
            value={companyInfo.mobile || ''}
            onChange={(value) => [
              dispatch(
                setCompanyFields({
                  fieldName: 'mobile',
                  fieldValue: value,
                }),
              ),
              formik.setFieldValue('mobile', value),
            ]}
            error={formik.errors.mobile && companyInfo?.mobile ? true : false}
            errorText={formik.errors.mobile}
            onBlur={formik.handleBlur}
          />
        </WhiteBox>
        <ButtonBox>
          <NavigationButton
            buttonType="prev"
            text={t('back')}
            onClick={() => navigate(`/${productType}-company-info`)}
          />
          <NavigationButton
            buttonType="next"
            text={t('send')}
            onClick={handleSendBtn}
            disabled={
              formik.isValid === true &&
              companyInfo.firstName &&
              companyInfo.lastName &&
              companyInfo.position &&
              companyInfo.email &&
              companyInfo.mobile
                ? false
                : true
            }
          />
        </ButtonBox>
      </Content>

      <BackgroundIcon />
    </Wrapper>
  )
}

export default CompanyContactInfo

const Wrapper = styled.div`
  background-color: ${Colors.backgroundGrey};
  min-height: 100vh;
`
const Content = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding-top: 60px;
  ${Responsive.mobile} {
    padding-top: 26px;
    padding-bottom: 48px;
  }
`
const WhiteBox = styled.div`
  margin-top: 32px;
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: 20px;
  background-color: ${Colors.white};
  width: 464px;
  box-shadow: 0 4px 50px 0 rgba(0, 0, 0, 0.04);
  border-radius: 8px;
  padding: 32px 48px;
  ${Responsive.mobile} {
    margin-top: 24px;
    padding: 24px 20px;
    max-width: 350px;
    width: 100%;
  }
`
const ButtonBox = styled.div`
  z-index: 2;
  display: flex;
  gap: 20px;
  margin-top: 40px;
  button {
    width: 174px;
  }

  ${Responsive.mobile} {
    button {
      width: 140px;
    }
  }
`

const SuccessModalWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
`
const BaseModalStyle = styled(BaseModal)`
  ${Responsive.mobile} {
    div {
      padding: 25px;
      background-color: red;
    }
  }
`
const SuccessIconStyle = styled(SuccessIcon)`
  background-color: transparent;
  width: 200px;
  height: 180px;
`
const Text = styled.div`
  text-align: center;
  font-size: 24px;
  color: ${Colors.primaryBlack};
  margin-top: 10px;
`
const LinkStyle = styled(Link)`
  font-size: 14px;
  color: ${Colors.primaryBlack};
  margin-top: 15px;
  &:hover {
    color: ${Colors.primaryBlack};
    opacity: 0.9;
  }
`
